import React, { useCallback, useEffect, useState } from "react";
import {
  CondicaoKey,
  CondicaoPar,
  RegraTributariaRequest,
  RegraTributariaResponse,
} from "../../../../domain/models/regra-tributaria";
import * as S from "./styles";

type Props = {
  visible: boolean;
  regra: RegraTributariaResponse | null;
  saving: boolean;
  onClose: () => void;
  onSave: (data: RegraTributariaRequest) => void;
};

const CONDICAO_KEYS: { value: CondicaoKey; label: string; type: "boolean" | "string" }[] = [
  { value: "tomador_estrangeiro", label: "Tomador Estrangeiro", type: "boolean" },
  { value: "natureza_operacao", label: "Natureza da Operação", type: "string" },
];

const NATUREZA_OPTIONS = ["bonificacao", "doacao", "remessa"];

const EMPTY_FORM: RegraTributariaRequest = {
  nome: "",
  descricao: "",
  prioridade: 0,
  condicao: [],
  cst: "",
  cclasstrib: "",
  cindop: "",
};

const RegraModal: React.FC<Props> = ({ visible, regra, saving, onClose, onSave }) => {
  const [form, setForm] = useState<RegraTributariaRequest>(EMPTY_FORM);

  useEffect(() => {
    if (regra) {
      setForm({
        nome: regra.nome,
        descricao: regra.descricao || "",
        prioridade: regra.prioridade,
        condicao: Array.isArray(regra.condicao) ? regra.condicao : [],
        cst: regra.cst,
        cclasstrib: regra.cclasstrib,
        cindop: regra.cindop,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [regra, visible]);

  const handleChange = useCallback((field: keyof RegraTributariaRequest, value: any): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // --- Condition editor ---
  const handleAddCondition = useCallback((): void => {
    setForm((prev) => ({
      ...prev,
      condicao: [...prev.condicao, { key: "tomador_estrangeiro", value: "true" }],
    }));
  }, []);

  const handleRemoveCondition = useCallback((index: number): void => {
    setForm((prev) => ({
      ...prev,
      condicao: prev.condicao.filter((_, i) => i !== index),
    }));
  }, []);

  const handleConditionKeyChange = useCallback((index: number, key: CondicaoKey): void => {
    setForm((prev) => {
      const updated = [...prev.condicao];
      const keyDef = CONDICAO_KEYS.find((k) => k.value === key);
      updated[index] = {
        key,
        value: keyDef?.type === "boolean" ? "true" : NATUREZA_OPTIONS[0],
      };
      return { ...prev, condicao: updated };
    });
  }, []);

  const handleConditionValueChange = useCallback((index: number, value: string): void => {
    setForm((prev) => {
      const updated = [...prev.condicao];
      updated[index] = { ...updated[index], value };
      return { ...prev, condicao: updated };
    });
  }, []);

  // --- Validation ---
  const cstValid = form.cst.length > 0;
  const cclasstribValid = form.cclasstrib.length > 0 && form.cclasstrib.startsWith(form.cst);
  const nomeValid = form.nome.trim().length > 0;

  const canSave = nomeValid && cstValid && cclasstribValid && form.cindop.length > 0;

  const handleSubmit = useCallback((): void => {
    if (!canSave) return;
    onSave(form);
  }, [canSave, form, onSave]);

  if (!visible) return null;

  const renderConditionValueInput = (cond: CondicaoPar, index: number): JSX.Element => {
    const keyDef = CONDICAO_KEYS.find((k) => k.value === cond.key);

    if (keyDef?.type === "boolean") {
      return (
        <S.FormSelect
          value={cond.value}
          onChange={(e) => handleConditionValueChange(index, e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="true">Sim (true)</option>
          <option value="false">Não (false)</option>
        </S.FormSelect>
      );
    }

    // natureza_operacao - string select
    return (
      <S.FormSelect
        value={cond.value}
        onChange={(e) => handleConditionValueChange(index, e.target.value)}
        style={{ flex: 1 }}
      >
        {NATUREZA_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </S.FormSelect>
    );
  };

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalTitle>{regra ? "Editar Regra Tributária" : "Nova Regra Tributária"}</S.ModalTitle>

        {/* Nome */}
        <S.FormGroup>
          <S.FormLabel>Nome *</S.FormLabel>
          <S.FormInput
            value={form.nome}
            onChange={(e) => handleChange("nome", e.target.value)}
            placeholder="Nome da regra"
            $error={!nomeValid && form.nome.length > 0}
          />
        </S.FormGroup>

        {/* Descrição */}
        <S.FormGroup>
          <S.FormLabel>Descrição</S.FormLabel>
          <S.FormTextarea
            value={form.descricao}
            onChange={(e) => handleChange("descricao", e.target.value)}
            placeholder="Descrição opcional da regra"
          />
        </S.FormGroup>

        {/* Prioridade */}
        <S.FormGroup>
          <S.FormLabel>Prioridade</S.FormLabel>
          <S.FormInput
            type="number"
            value={form.prioridade}
            onChange={(e) => handleChange("prioridade", Number(e.target.value))}
            placeholder="0"
          />
        </S.FormGroup>

        {/* CST + cClassTrib + cIndOp */}
        <S.FormRow>
          <S.FormGroup style={{ flex: 1 }}>
            <S.FormLabel>CST *</S.FormLabel>
            <S.FormInput
              value={form.cst}
              onChange={(e) => handleChange("cst", e.target.value)}
              placeholder="Ex: 00"
            />
          </S.FormGroup>
          <S.FormGroup style={{ flex: 1 }}>
            <S.FormLabel>cClassTrib *</S.FormLabel>
            <S.FormInput
              value={form.cclasstrib}
              onChange={(e) => handleChange("cclasstrib", e.target.value)}
              placeholder="Ex: 001001"
              $error={form.cclasstrib.length > 0 && !cclasstribValid}
            />
            {form.cclasstrib.length > 0 && (
              <S.ValidationHint $valid={cclasstribValid}>
                {cclasstribValid
                  ? "✓ Prefixo CST válido"
                  : `✗ cClassTrib deve iniciar com o CST "${form.cst}"`}
              </S.ValidationHint>
            )}
          </S.FormGroup>
          <S.FormGroup style={{ flex: 1 }}>
            <S.FormLabel>cIndOp *</S.FormLabel>
            <S.FormInput
              value={form.cindop}
              onChange={(e) => handleChange("cindop", e.target.value)}
              placeholder="Ex: 01"
            />
          </S.FormGroup>
        </S.FormRow>

        {/* Condições */}
        <S.FormGroup>
          <S.FormLabel>Condições</S.FormLabel>
          <S.ConditionEditorWrapper>
            {form.condicao.length === 0 && (
              <S.LoadingText style={{ padding: "8px", fontSize: "12px" }}>
                Nenhuma condição definida. A regra será aplicada como padrão.
              </S.LoadingText>
            )}
            {form.condicao.map((cond, index) => (
              <S.ConditionRow key={index}>
                <S.FormSelect
                  value={cond.key}
                  onChange={(e) => handleConditionKeyChange(index, e.target.value as CondicaoKey)}
                  style={{ width: "180px", flex: "none" }}
                >
                  {CONDICAO_KEYS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </S.FormSelect>
                {renderConditionValueInput(cond, index)}
                <S.RemoveConditionButton
                  type="button"
                  onClick={() => handleRemoveCondition(index)}
                  title="Remover condição"
                >
                  ×
                </S.RemoveConditionButton>
              </S.ConditionRow>
            ))}
            <S.AddConditionButton type="button" onClick={handleAddCondition}>
              + Adicionar condição
            </S.AddConditionButton>
          </S.ConditionEditorWrapper>
        </S.FormGroup>

        {/* Footer */}
        <S.ModalFooter>
          <S.ModalButton type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </S.ModalButton>
          <S.ModalButton
            type="button"
            $primary
            onClick={handleSubmit}
            disabled={!canSave || saving}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {saving ? "Salvando..." : regra ? "Salvar" : "Criar"}
          </S.ModalButton>
        </S.ModalFooter>
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

export default RegraModal;
